import {useCallback} from 'react';
import clsx from 'clsx';
import {ProductFilter} from './ProductFilter.client';
import {OptionsDropdown, Drawer} from '~/components';
import {SORT_OPTIONS} from '~/lib/const';
import {FilterStateType, FilterOption} from '~/types';

function ProductDrawerSection({
  className,
  title,
  children,
}: {
  className?: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className={clsx('flex flex-col w-full', className)}>
      <div className={'flex'}>
        <h1 className={'font-header font-bold text-16/20'}>{title}</h1>
      </div>
      <div className={'mt-5'}>{children}</div>
    </div>
  );
}

function ProductSort({
  sort,
  onChangeSort,
}: {
  sort: string;
  onChangeSort: (value: string) => void;
}) {
  const sortLabel =
    SORT_OPTIONS.find((s) => s.value === sort)?.labelMobile ??
    SORT_OPTIONS[0].labelMobile;
  const handleChange = useCallback(
    (_: string, value: string) => {
      const sortKey = SORT_OPTIONS.find(
        (option) => option.labelMobile === value,
      )?.value;

      if (sortKey == null) {
        console.error(`ProductSort: can't find sort key of label: ${value}`);
        return;
      }

      onChangeSort(sortKey);
    },
    [onChangeSort],
  );

  return (
    <div className={'relative h-[54px]'}>
      <OptionsDropdown
        name={'sort'}
        classNameButton={'font-sans font-medium text-18/26 py-3 px-7 md:w-full'}
        selectedValue={sortLabel}
        values={SORT_OPTIONS.map((o) => o.labelMobile)}
        sortDisabled
        onChange={handleChange}
      />
    </div>
  );
}

export function ProductFilterDrawer({
  sort,
  title,
  isOpen,
  filterApplied,
  options,
  onClickApplyFilters,
  onClickClearFilters,
  onChangeSort,
  onClose,
}: {
  sort: string;
  title: string;
  isOpen: boolean;
  filterApplied: FilterStateType;
  options: FilterOption[];
  onClickApplyFilters: (filterState: FilterStateType) => void;
  onClickClearFilters: () => void;
  onChangeSort: (value: string) => void;
  onClose: () => void;
}) {
  const handleClickApplyFilters = useCallback(
    (filterState: FilterStateType) => {
      onClickApplyFilters(filterState);
    },
    [onClickApplyFilters],
  );

  const handleClickClearFilters = useCallback(() => {
    onClickClearFilters();
    onClose();
  }, [onClickClearFilters, onClose]);

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      openFrom="left"
      heading={title}
      classNameHeader={'p-5 bg-white z-[100]'}
      classNameCloseButton={'bg-gray-200 rounded'}
      headingClassname="text-34/46 lg:text-32/44 font-extrabold font-header"
      classNamePanel={'max-w-lg'}
    >
      <div className="px-5 pt-10 pb-[66px]">
        <ProductDrawerSection title={'SORT BY'}>
          <ProductSort sort={sort} onChangeSort={onChangeSort} />
        </ProductDrawerSection>
        <ProductDrawerSection title={'FILTERS'} className={'mt-10'}>
          <ProductFilter
            filterApplied={filterApplied}
            options={options}
            onClickApplyFilters={handleClickApplyFilters}
          />
        </ProductDrawerSection>
        <div className={'flex flex-col justify-center items-center'}>
          <button
            type={'button'}
            className={
              'text-16/22 font-bold font-header tracking-wider text-center text-gray-400 p-2.5 mt-2.5'
            }
            onClick={handleClickClearFilters}
          >
            CLEAR ALL
          </button>
        </div>
      </div>
    </Drawer>
  );
}
